import { MapboxOverlay, MapboxOverlayProps } from '@deck.gl/mapbox';
import { Map, Marker, useControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapLayerSettings, VariableDataFrame } from '@app/api/mapping/types';
import * as WeatherLayers from 'weatherlayers-gl';
import { useEffect, useRef } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import { MultiLayerTooltipControl } from './MultiLayerTooltipControl';

export interface MapLayer {
  readonly component: MapLayerSettings;
  readonly data: VariableDataFrame;
}

export interface WeatherMapProps {
  layers: (
    | WeatherLayers.RasterLayer
    | WeatherLayers.ContourLayer
    | WeatherLayers.GridLayer
    | WeatherLayers.HighLowLayer
    | WeatherLayers.ParticleLayer
  )[];
  tooltipControl: MultiLayerTooltipControl;
  onClick: (event: any) => any;
  selectedCoordinates?: { lat: number; lon: number } | null;
  onLocationSelect?: (lat: number, lon: number, name: string) => void;
}

interface OverlayProps extends MapboxOverlayProps {
  tooltipControl: MultiLayerTooltipControl;
}

function DeckGLOverlay({ tooltipControl, ...props }: OverlayProps) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  const parentElement = overlay.getCanvas()?.parentElement?.parentElement;
  useEffect(() => {
    if (parentElement) {
      tooltipControl.addTo(parentElement);
      return () => {
        tooltipControl.remove();
      };
    }
  }, [parentElement, tooltipControl]);
  overlay.setProps(props);
  return null;
}

export default function WeatherMap({
  layers,
  tooltipControl,
  selectedCoordinates,
  onClick,
}: WeatherMapProps) {
  const mapRef = useRef<MapRef | null>(null);

  // Fly to new coordinates when selectedCoordinates changes
  useEffect(() => {
    if (selectedCoordinates && mapRef.current) {
      mapRef.current.flyTo({
        center: [selectedCoordinates.lon, selectedCoordinates.lat],
        essential: true,
        offset: [0, -100],
        zoom: 8,
        duration: 2000,
      });
    }
  }, [selectedCoordinates]);

  return (
    <div className="relative h-full w-full">
      <Map
        ref={mapRef}
        mapStyle="https://tiles.basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
        maplibreLogo={false}
        attributionControl={false}
        dragRotate={false}
        initialViewState={{
          zoom: 1.25,
          latitude: 28.009,
          longitude: -9.083,
        }}
      >
        <DeckGLOverlay
          layers={layers}
          interleaved={true}
          onHover={(event) => {
            tooltipControl.updatePickingInfo(event);
          }}
          onClick={onClick}
          tooltipControl={tooltipControl}
        />
        {selectedCoordinates && (
          <Marker
            color="#EC4899"
            latitude={selectedCoordinates.lat}
            longitude={selectedCoordinates.lon}
            anchor="bottom"
          >
            <div className="relative flex items-center justify-center">
              {/* Outer pulse ring */}
              <div className="absolute w-8 h-8 bg-pink-500/20 rounded-full animate-ping" />
              {/* Middle ring */}
              <div className="absolute w-6 h-6 bg-pink-500/40 rounded-full" />
              {/* Inner dot */}
              <div className="relative w-4 h-4 bg-pink-500 rounded-full border-2 border-white shadow-lg" />
            </div>
          </Marker>
        )}
      </Map>
    </div>
  );
}
